/**
 * Servicio para manejar tokens JWT
 * Responsabilidad única: Gestionar operaciones relacionadas con tokens
 */
export class TokenService {
  constructor() {
    this.tokenKey = 'x-token'
    this.tokenReceivedKey = 'x-token-received'
  }

  /**
   * Obtiene el token raw desde localStorage
   * @returns {string|null} Token raw o null si no existe
   */
  getRawToken() {
    return localStorage.getItem(this.tokenKey)
  }

  /**
   * Obtiene el token limpio (sin Bearer prefix)
   * @returns {string|null} Token limpio o null si no existe
   */
  getCleanToken() {
    const tokenRaw = this.getRawToken()
    if (!tokenRaw) return null

    return tokenRaw.replace(/^Bearer\s+/, '')
  }

  /**
   * Decodifica el payload del token JWT
   * @returns {Object|null} Payload decodificado o null si hay error
   */
  decodeTokenPayload() {
    const token = this.getCleanToken()
    if (!token) return null

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload
    } catch (error) {
      console.error('Error al decodificar token:', error)
      return null
    }
  }

  /**
   * Obtiene el timestamp de expiración del token
   * @returns {number|null} Timestamp de expiración o null si hay error
   */
  getTokenExpiration() {
    const payload = this.decodeTokenPayload()
    return payload && payload.exp ? payload.exp : null
  }

  /**
   * Obtiene el timestamp de recepción del token
   * @returns {number|null} Timestamp de recepción o null si no existe
   */
  getTokenReceivedTimestamp() {
    const receivedMs = localStorage.getItem(this.tokenReceivedKey)
    return receivedMs ? Math.floor(Number(receivedMs) / 1000) : null
  }

  /**
   * Calcula los segundos restantes hasta la expiración
   * @returns {number} Segundos restantes (0 si ya expiró)
   */
  getSecondsUntilExpiration() {
    const exp = this.getTokenExpiration()
    if (!exp) return 0

    const now = Math.floor(Date.now() / 1000)
    const secondsLeft = exp - now

    return Math.max(0, secondsLeft)
  }

  /**
   * Calcula el tiempo total de vida del token
   * @returns {number} Tiempo total en segundos o 0 si no se puede calcular
   */
  getTotalTokenLifetime() {
    const exp = this.getTokenExpiration()
    const received = this.getTokenReceivedTimestamp()

    if (!exp || !received) return 0

    return exp - received
  }

  /**
   * Calcula el tiempo transcurrido desde que se recibió el token
   * @returns {number} Tiempo transcurrido en segundos
   */
  getElapsedTime() {
    const received = this.getTokenReceivedTimestamp()
    if (!received) return 0

    const now = Math.floor(Date.now() / 1000)
    return now - received
  }

  /**
   * Calcula el porcentaje de tiempo transcurrido del token
   * @returns {number} Porcentaje de 0 a 100
   */
  getTokenUsagePercentage() {
    const totalTime = this.getTotalTokenLifetime()
    if (totalTime === 0) return 0

    const elapsed = this.getElapsedTime()
    return Math.min(100, Math.max(0, (elapsed / totalTime) * 100))
  }

  /**
   * Verifica si el token ha expirado
   * @returns {boolean} True si ha expirado, false en caso contrario
   */
  isTokenExpired() {
    return this.getSecondsUntilExpiration() <= 0
  }

  /**
   * Verifica si el token está próximo a expirar
   * @param {number} thresholdSeconds - Umbral en segundos para considerar "próximo a expirar"
   * @returns {boolean} True si está próximo a expirar
   */
  isTokenNearExpiration(thresholdSeconds = 60) {
    const secondsLeft = this.getSecondsUntilExpiration()
    return secondsLeft > 0 && secondsLeft <= thresholdSeconds
  }

  /**
   * Formatea los segundos restantes como MM:SS
   * @param {number} seconds - Segundos a formatear
   * @returns {string} Tiempo formateado como MM:SS
   */
  formatSecondsAsMinutes(seconds) {
    if (seconds <= 0) return '0:00'

    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  /**
   * Limpia todos los datos del token
   */
  clearTokenData() {
    localStorage.removeItem(this.tokenKey)
    localStorage.removeItem(this.tokenReceivedKey)
  }

  /**
   * Verifica si existe un token válido
   * @returns {boolean} True si existe un token válido
   */
  hasValidToken() {
    const token = this.getRawToken()
    const payload = this.decodeTokenPayload()

    return !!(token && payload && !this.isTokenExpired())
  }
}

// Instancia singleton para uso global
export const tokenService = new TokenService()
